import { TERMINAL_DATA } from '../constants/terminalConstants';
import { COLORI_MAPPINGS } from '../constants/colorConstants'; // Import COLORI_MAPPINGS for getColorsForLanguage
import { translations } from '../i18n/translations';
import { convertAWGToMetric, convertMetricToAWG, formatSezione } from './cableLogic';

// Helper to get colors based on language, replicating original logic
const getColorsForLanguage = (lingua) => {
  return COLORI_MAPPINGS[lingua] || COLORI_MAPPINGS.it; // Default to Italian
};

const getTranslation = (lingua, key, params = {}) => {
  const lang = (lingua && translations[lingua]) ? lingua : 'it';
  const group = translations[lang]?.validation || translations['it'].validation;
  let text = group[key] || key;
  Object.keys(params).forEach(k => {
    text = text.replace(`{${k}}`, params[k]);
  });
  return text;
};

export const validateTerminal = (terminalCode, lingua) => {
  if (!terminalCode || terminalCode.trim() === '') {
    return { isValid: false, error: getTranslation(lingua, 'emptyTerminal') };
  }

  if (terminalCode.startsWith('SP') && terminalCode.length > 2) {
    // Nuovo formato SPx/y
    const match = terminalCode.match(/^SP(\d+)\/(\d+)$/);
    if (match) {
      const length = parseInt(match[1]);
      const peel = parseInt(match[2]);
      if (isNaN(length) || length < 0 || length > 100) {
        return { isValid: false, error: getTranslation(lingua, 'invalidSPLength', { length: match[1] }) };
      }
      if (isNaN(peel) || peel < 0 || peel > 50) {
        return { isValid: false, error: getTranslation(lingua, 'invalidSPPeel', { peel: match[2] }) };
      }
      return { isValid: true, error: null };
    }

    // Fallback per formato vecchio SPx (compatibilità)
    const lengthPart = terminalCode.substring(2);
    const length = parseInt(lengthPart);
    if (isNaN(length) || length <= 0 || length > 100) {
      return { isValid: false, error: getTranslation(lingua, 'invalidSPLength', { length: lengthPart }) };
    }
    return { isValid: true, error: null };
  }

  if (!TERMINAL_DATA[terminalCode]) {
    return { isValid: false, error: getTranslation(lingua, 'unknownTerminal', { terminal: terminalCode }) };
  }

  return { isValid: true, error: null };
};

export const validateColor = (color, lingua) => {
  if (!color || color.trim() === '') {
    return { isValid: false, error: getTranslation(lingua, 'emptyColor') };
  }

  const availableColors = getColorsForLanguage(lingua);
  if (!availableColors.includes(color)) {
    return { isValid: false, error: getTranslation(lingua, 'unknownColor', { color: color }) };
  }

  return { isValid: true, error: null };
};

export const checkIfCableIsDuplicate = (targetCable, cables) => {
  const duplicateIndexes = [];

  // Funzione helper per normalizzare i valori per il confronto (semplificata)
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    return value;
  };


  cables.forEach((cable, index) => {
    // Controllo più preciso per evitare confronti con se stesso
    const isSameCable = (
      (cable === targetCable) || // Stesso riferimento oggetto (più affidabile)
      (cable.originalIndex !== undefined && targetCable.originalIndex !== undefined && cable.originalIndex === targetCable.originalIndex) // Stesso originalIndex solo se entrambi esistono
    );

    if (isSameCable) {
      duplicateIndexes.push(index); // Include se stesso
      return;
    }

    // Confronto dettagliato campo per campo
    const fieldComparisons = {
      siglatura: cable.siglatura === targetCable.siglatura,
      colore: cable.colore === targetCable.colore,
      sezione: cable.sezione === targetCable.sezione,
      lunghezza: cable.lunghezza === targetCable.lunghezza,
      terminaleA: cable.terminaleA === targetCable.terminaleA,
      terminaleB: cable.terminaleB === targetCable.terminaleB,
      collegamentoA: cable.collegamentoA === targetCable.collegamentoA,
      collegamentoB: cable.collegamentoB === targetCable.collegamentoB,
      gruppo: cable.gruppo === targetCable.gruppo,
      guaina: cable.guaina === targetCable.guaina
    };

    const areIdentical = Object.values(fieldComparisons).every(Boolean);

    if (areIdentical) {
      duplicateIndexes.push(index);
    }
  });

  const result = {
    isDuplicate: duplicateIndexes.length > 1,
    duplicateIndexes: duplicateIndexes
  };


  return result;
};


// Valida la compatibilità sezione-terminale.
// sezioniAlCollegamento: array delle sezioni (mm² o AWG) di tutti i cavi che condividono
// lo stesso collegamento+terminale. Per terminali singoli è [sezione], per doppi può essere [s1, s2].
// La funzione è identica per tutti i tipi grazie al campo maxSection già totalizzato.
export const validateTerminalSection = (terminalCode, sezioniAlCollegamento, terminali = null, lingua = 'it', useAWG = false) => {
  const termData = terminali || TERMINAL_DATA;
  const config = termData[terminalCode];
  if (!config || (config.minSection == null && config.maxSection == null)) return null;

  const sezioni = sezioniAlCollegamento.map(s => {
    let num = parseFloat(s);
    if (useAWG && !isNaN(num)) {
      num = parseFloat(convertAWGToMetric(num));
    }
    return num;
  }).filter(s => s != null && !isNaN(s));
  const sum = Math.round(sezioni.reduce((a, b) => a + b, 0) * 100) / 100;

  // Controlla che la somma rispetti il minimo
  if (config.minSection != null && sum < config.minSection) {
    const minDisplay = useAWG ? convertMetricToAWG(config.minSection) : config.minSection;
    const sumDisplay = useAWG ? convertMetricToAWG(sum) : sum;
    return getTranslation(lingua, 'sectionUnderMin', { 
      terminal: terminalCode, 
      sezione: formatSezione(sumDisplay, useAWG), 
      min: formatSezione(minDisplay, useAWG) 
    });
  }

  // Controlla che la somma totale non superi il massimo
  if (config.maxSection != null && sum > config.maxSection) {
    const sumDisplay = useAWG ? convertMetricToAWG(sum) : sum;
    const maxDisplay = useAWG ? convertMetricToAWG(config.maxSection) : config.maxSection;
    return getTranslation(lingua, 'sectionOverMaxTotal', { 
      terminal: terminalCode, 
      sum: formatSezione(sumDisplay, useAWG), 
      max: formatSezione(maxDisplay, useAWG) 
    });
  }

  return null;
};

export const validateCable = (cable, cables, lingua, useAWG = false) => {
  const issues = [];

  if (cable.poli && Array.isArray(cable.poli)) {
    if (!cable.nomeCavo || cable.nomeCavo.trim() === '') {
      issues.push(getTranslation(lingua, 'cableNameMissing'));
    }

    cable.poli.forEach((polo, index) => {
      const termAValidation = validateTerminal(polo.terminaleA, lingua);
      if (!termAValidation.isValid) {
        issues.push(getTranslation(lingua, 'poleError', { index: index + 1, error: `${getTranslation(lingua, 'terminalA')}: ${termAValidation.error}` }));
      }

      const termBValidation = validateTerminal(polo.terminaleB, lingua);
      if (!termBValidation.isValid) {
        issues.push(getTranslation(lingua, 'poleError', { index: index + 1, error: `${getTranslation(lingua, 'terminalB')}: ${termBValidation.error}` }));
      }

      const colorValidation = validateColor(polo.colore, lingua);
      if (!colorValidation.isValid) {
        issues.push(getTranslation(lingua, 'poleError', { index: index + 1, error: `${getTranslation(lingua, 'color')}: ${colorValidation.error}` }));
      }
    });
  } else { // Standard cable validation
    const termAValidation = validateTerminal(cable.terminaleA, lingua);
    if (!termAValidation.isValid) {
      issues.push(getTranslation(lingua, 'terminalAError', { error: termAValidation.error }));
    }

    const termBValidation = validateTerminal(cable.terminaleB, lingua);
    if (!termBValidation.isValid) {
      issues.push(getTranslation(lingua, 'terminalBError', { error: termBValidation.error }));
    }

    const colorValidation = validateColor(cable.colore, lingua);
    if (!colorValidation.isValid) {
      issues.push(getTranslation(lingua, 'colorError', { error: colorValidation.error }));
    }

    if (!cable.sezione || cable.sezione <= 0) {
      issues.push(getTranslation(lingua, 'invalidSection'));
    }

    // Validazione compatibilità sezione-terminale
    // Il controllo min viene fatto sempre sul singolo cavo (anche senza collegamento).
    // Il controllo max viene fatto sulla somma del gruppo se c'è un collegamento,
    // oppure sul singolo cavo se il collegamento è vuoto.
    if (cable.sezione && cable.sezione > 0) {
      const validateSide = (terminalCode, collegamento, getCableTerminal, getCableCollegamento) => {
        if (!terminalCode) return;
        const config = TERMINAL_DATA[terminalCode];
        if (!config || (config.minSection == null && config.maxSection == null)) return;

        let originalSezione = cable.sezione;
        let sezione = parseFloat(cable.sezione);
        if (useAWG && !isNaN(sezione)) {
          sezione = parseFloat(convertAWGToMetric(originalSezione));
        }
        let sum = sezione;
        let isGrouped = false;

        if (collegamento && Array.isArray(cables) && config.allowsMultiple !== false) {
          // Con collegamento e terminale multiplo ammesso: calcola somma di tutti i cavi
          const sezioniGruppo = cables
            .filter(c => getCableCollegamento(c) === collegamento && getCableTerminal(c) === terminalCode && c.siglatura === cable.siglatura)
            .map(c => {
              let num = parseFloat(c.sezione);
              if (useAWG && !isNaN(num)) {
                num = parseFloat(convertAWGToMetric(c.sezione));
              }
              return num;
            })
            .filter(s => !isNaN(s));
          if (sezioniGruppo.length > 0) {
            sum = Math.round(sezioniGruppo.reduce((a, b) => a + b, 0) * 100) / 100;
            isGrouped = true;
          }
        }

        // 1. Controllo minimo: sulla somma se raggruppati, o sul singolo
        if (config.minSection != null && sum < config.minSection) {
          const configMin = useAWG ? convertMetricToAWG(config.minSection) : config.minSection;
          const sumDisplayVal = isGrouped ? (useAWG ? convertMetricToAWG(sum) : sum) : originalSezione;
          issues.push(getTranslation(lingua, 'sectionUnderMin', { 
            terminal: terminalCode, 
            sezione: formatSezione(sumDisplayVal, useAWG), 
            min: formatSezione(configMin, useAWG) 
          }));
          return; // Inutile proseguire
        }

        // 2. Controllo massimo
        if (config.maxSection != null && sum > config.maxSection) {
          const configMax = useAWG ? convertMetricToAWG(config.maxSection) : config.maxSection;
          if (isGrouped) {
            issues.push(getTranslation(lingua, 'sectionOverMaxTotal', { 
              terminal: terminalCode, 
              sum: formatSezione(useAWG ? convertMetricToAWG(sum) : sum, useAWG), 
              max: formatSezione(configMax, useAWG) 
            }));
          } else {
            issues.push(getTranslation(lingua, 'sectionOverMaxSingle', { 
              terminal: terminalCode, 
              sezione: formatSezione(originalSezione, useAWG), 
              max: formatSezione(configMax, useAWG) 
            }));
          }
        }
      };

      validateSide(cable.terminaleA, cable.collegamentoA, c => c.terminaleA, c => c.collegamentoA);
      validateSide(cable.terminaleB, cable.collegamentoB, c => c.terminaleB, c => c.collegamentoB);
    }

    // Controlla duplicati solo per cavi standard
    const duplicateCheck = checkIfCableIsDuplicate(cable, cables);
    if (duplicateCheck.isDuplicate) {
      const otherNumbers = duplicateCheck.duplicateIndexes
        .filter(idx => idx !== cable.originalIndex)
        .map(idx => `#${idx + 1}`)
        .join(', ');
      issues.push(getTranslation(lingua, 'duplicateCableOf', { cables: otherNumbers }));
    }
  }

  return {
    isValid: issues.length === 0,
    issues: issues // Return issues to be displayed
  };
};

export const validateCableFields = (cable, cables, lingua) => {
  const missingFields = [];

  if (cable.poli && Array.isArray(cable.poli) && cable.poli.length > 0) {
    if (!cable.nomeCavo || cable.nomeCavo.trim() === '') {
      missingFields.push('nome cavo');
    }
    cable.poli.forEach((polo, index) => {
      if (!polo.collegamentoA || polo.collegamentoA.trim() === '') {
        missingFields.push(`collegamento A per polo ${index + 1}`);
      }
      if (!polo.collegamentoB || polo.collegamentoB.trim() === '') {
        missingFields.push(`collegamento B per polo ${index + 1}`);
      }
    });
  } else {
    if (!cable.siglatura || (typeof cable.siglatura === 'string' && cable.siglatura.trim() === '') || (typeof cable.siglatura !== 'string' && !cable.siglatura)) {
      missingFields.push('siglatura');
    }
    if (!cable.collegamentoA || cable.collegamentoA.trim() === '') {
      missingFields.push('collegamento A');
    }
    if (!cable.collegamentoB || cable.collegamentoB.trim() === '') {
      missingFields.push('collegamento B');
    }
  }
  return missingFields;
};